import { useMutation } from '@tanstack/react-query';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { auth } from 'src/config/firebase';
import ApiConfig from 'src/config/api-config';
import { IAuthSignUpPayload } from '../interfaces/auth';

export function useAuthSignUp() {
  return useMutation({
    mutationFn: async (payload: IAuthSignUpPayload) => {
      const { name, surname, email, password } = payload;

      // 1. Create a user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 2. Update Firebase Auth profile
      await updateProfile(user, {
        displayName: `${name.trim()} ${surname.trim()}`.trim(),
      });

      // 3. Create a user profile in Backend first
      const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.CREATE_USER_PROFILE_API_HOST}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          name: name.trim(),
          surname: surname.trim(),
        }),
      });

      if (!response.ok) {
        // Parse the error message
        let errorMsg = 'Unknown error';
        try {
          const errData = await response.json();
          errorMsg = errData.error || errorMsg;
        } catch {
          errorMsg = await response.text();
        }

        // Backend validation failed, so we delete the Firebase Auth user
        // to prevent ghost accounts that cannot log in properly
        await user.delete().catch(console.error);

        throw new Error(errorMsg);
      }

      // 4. Send email verification ONLY AFTER backend profile is successfully created
      const basePath = process.env.APP_BASENAME || '';
      await sendEmailVerification(user, {
        url: `${window.location.origin}${basePath}/onboarding-trainer/login?type=trainer`,
      });

      return user;
    },
  });
}
